import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Button,
  Divider
} from '@mui/material';
import {
  Clear as ClearIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const FilterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const campaignGoalOptions = [
  { value: 'all', label: 'All Goals' },
  { value: 'product_launch', label: 'Product Launch' },
  { value: 'brand_awareness', label: 'Brand Awareness' },
  { value: 'event_promotion', label: 'Event Promotion' },
  { value: 'sales_promotion', label: 'Sales/Promotion' },
  { value: 'content_promotion', label: 'Content Promotion' }
];

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'ready', label: 'Ready to Schedule' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' }
];

const dateRangeOptions = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

const CampaignFilters = ({ filters, onChange, sx }) => {
  const handleFilterChange = (field, value) => {
    onChange({
      ...filters,
      [field]: value
    });
  };

  const handleClearAll = () => {
    onChange({
      status: 'all',
      goal: 'all',
      dateRange: 'all'
    });
  };

  const hasActiveFilters = Object.values(filters).some(filter => filter !== 'all');

  const activeFilterCount = Object.values(filters).filter(filter => filter !== 'all').length;

  return (
    <FilterContainer elevation={1} sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon color="primary" />
          <Typography variant="h6">
            Advanced Filters
          </Typography>
          {hasActiveFilters && (
            <Chip 
              label={`${activeFilterCount} active`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
        
        {hasActiveFilters && (
          <Button
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            size="small"
            color="error"
          >
            Clear All
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <FilterSection>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Campaign Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filters.status}
                label="Campaign Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterSection>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FilterSection>
            <FormControl fullWidth size="small">
              <InputLabel id="goal-filter-label">Campaign Goal</InputLabel>
              <Select
                labelId="goal-filter-label"
                value={filters.goal}
                label="Campaign Goal"
                onChange={(e) => handleFilterChange('goal', e.target.value)}
              >
                {campaignGoalOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterSection>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FilterSection>
            <FormControl fullWidth size="small">
              <InputLabel id="date-filter-label">Date Range</InputLabel>
              <Select
                labelId="date-filter-label"
                value={filters.dateRange}
                label="Date Range"
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              >
                {dateRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterSection>
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Active Filters:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.status !== 'all' && (
              <Chip
                label={`Status: ${statusOptions.find(o => o.value === filters.status)?.label}`}
                onDelete={() => handleFilterChange('status', 'all')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {filters.goal !== 'all' && (
              <Chip
                label={`Goal: ${campaignGoalOptions.find(o => o.value === filters.goal)?.label}`}
                onDelete={() => handleFilterChange('goal', 'all')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {filters.dateRange !== 'all' && (
              <Chip
                label={`Date: ${dateRangeOptions.find(o => o.value === filters.dateRange)?.label}`}
                onDelete={() => handleFilterChange('dateRange', 'all')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      )}
    </FilterContainer>
  );
};

export default CampaignFilters; 